/**
 * useUploadStatus Hook
 *
 * Provides reactive upload status for overlays.
 * Used to disable features that require Convex URLs during upload.
 */

import { useState, useCallback, useEffect } from 'react';
import { Overlay } from '../types';
import { getUploadStatusForOverlays, UploadProgress } from '../utils/general/upload-status-checker';
import { findMediaBySrc } from '../utils/general/indexdb';

interface UseUploadStatusResult {
  /** Whether any overlays have pending uploads */
  hasPendingUploads: boolean;
  /** Number of pending uploads */
  pendingCount: number;
  /** Whether the status is currently being checked */
  isChecking: boolean;
  /** Progress information */
  progress: UploadProgress;
  /** Manually check upload status */
  checkStatus: () => Promise<boolean>;
}

/**
 * Hook to track upload status for overlays
 *
 * @param overlays - Array of overlays to check
 * @param refreshInterval - Interval to refresh status (default: 2000ms, 0 to disable)
 */
export const useUploadStatus = (
  overlays: Overlay[],
  refreshInterval: number = 2000
): UseUploadStatusResult => {
  const [hasPendingUploads, setHasPendingUploads] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [progress, setProgress] = useState<UploadProgress>({
    total: 0,
    completed: 0,
    pending: 0,
    failed: 0,
  });

  const checkStatus = useCallback(async (): Promise<boolean> => {
    setIsChecking(true);
    try {
      const status = await getUploadStatusForOverlays(overlays);
      setHasPendingUploads(status.hasPendingUploads);
      setPendingCount(status.progress.pending);
      setProgress(status.progress);
      return status.hasPendingUploads;
    } catch (error) {
      console.error('[useUploadStatus] Error checking status:', error);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [overlays]);

  // Initial check
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Periodic refresh
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      checkStatus();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [checkStatus, refreshInterval]);

  return {
    hasPendingUploads,
    pendingCount,
    isChecking,
    progress,
    checkStatus,
  };
};

/**
 * Hook to check if a specific overlay's video is still uploading
 *
 * @param src - The overlay's src URL
 * @param refreshInterval - Interval to refresh status (default: 2000ms)
 */
export const useOverlayUploadStatus = (
  src: string | undefined,
  refreshInterval: number = 2000
): {
  isUploading: boolean;
  uploadStatus: string | undefined;
  checkStatus: () => Promise<void>;
} => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | undefined>(undefined);

  const checkStatus = useCallback(async () => {
    if (!src) {
      setIsUploading(false);
      setUploadStatus(undefined);
      return;
    }

    // Only check blob URLs - Convex URLs are already uploaded
    if (!src.startsWith('blob:')) {
      setIsUploading(false);
      setUploadStatus('completed');
      return;
    }

    try {
      const mediaItem = await findMediaBySrc(src);
      if (mediaItem) {
        const isPending =
          mediaItem.uploadStatus === 'pending' ||
          mediaItem.uploadStatus === 'uploading';
        setIsUploading(isPending);
        setUploadStatus(mediaItem.uploadStatus);
      } else {
        setIsUploading(false);
        setUploadStatus(undefined);
      }
    } catch (error) {
      console.error('[useOverlayUploadStatus] Error checking status:', error);
      setIsUploading(false);
    }
  }, [src]);

  // Initial check
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Periodic refresh
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      checkStatus();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [checkStatus, refreshInterval]);

  return { isUploading, uploadStatus, checkStatus };
};
